import { all, call, put, race, select } from 'redux-saga/effects';

import { GeneratorResult, OperationStatus, takeIf } from '@pressreader/appstore';
import { getIssueLayout, mapIssueLayoutDto } from '@pressreader/content-api';
import { IIssueInfo, IssueLayout } from '@pressreader/content-types';
import { error } from '@pressreader/logger';
import { ExtractReturnType } from '@pressreader/types';

import { loadIssueLayoutBeganEvent, loadIssueLayoutDoneEvent, loadIssueLayoutFailedEvent } from '../../actions/layout';
import { selectIssueLayoutByCidDateKey } from '../../selectors/layout';

function* getOrWaitForIssueLayout(cidDateKey: string): GeneratorResult<IssueLayout | null> {
    const data: ReturnType<typeof selectIssueLayoutByCidDateKey> = yield select(selectIssueLayoutByCidDateKey, cidDateKey);

    if (data && data.loading !== OperationStatus.None) {
        if (data.loading === OperationStatus.Done) {
            return data.layout;
        }

        if (data.loading === OperationStatus.Failed) {
            return null;
        }

        yield race({
            done: takeIf(loadIssueLayoutDoneEvent, a => a.payload.cidDateKey === cidDateKey),
            failed: takeIf(loadIssueLayoutFailedEvent, a => a.payload.cidDateKey === cidDateKey),
        });

        return yield getOrWaitForIssueLayout(cidDateKey);
    }

    return null;
}

function* loadIssueLayout(issueInfo: IIssueInfo): GeneratorResult<IssueLayout | null> {
    const { issueCidDateKey: cidDateKey } = issueInfo;

    let issueLayout: ExtractReturnType<typeof getOrWaitForIssueLayout> = yield getOrWaitForIssueLayout(cidDateKey);
    if (issueLayout) {
        return issueLayout;
    }

    yield put(loadIssueLayoutBeganEvent({ cidDateKey }));

    try {
        const [issueLayoutDto]: [ExtractReturnType<typeof getIssueLayout>] = yield all([
            call(() => getIssueLayout({ issue: issueInfo.issueKey, version: issueInfo.layout.version, expungeVersion: issueInfo.expungeVersion })),
        ]);

        issueLayout = mapIssueLayoutDto(issueLayoutDto);

        yield put(loadIssueLayoutDoneEvent({ cidDateKey, layout: issueLayout }));

        return issueLayout;
    } catch (err) {
        error('Failed to load issue layout', err);
        yield put(loadIssueLayoutFailedEvent({ cidDateKey }));
    }

    return null;
}

export { loadIssueLayout };
